import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  IDENTITY_PROVIDER_LABEL,
  PAYMENT_HISTORY_SCOPES,
  REFUND_ARRIVAL_NOTICE,
  UNMADE_SHEET_RETURN_NOTICE,
  formatDateTime,
  formatKrw,
  needsYear,
  pendingAmendment,
  paymentHistoryChip,
  paymentHistoryDetailLine,
  paymentHistoryOccurredAt,
  paymentMonthLabel,
  refundedImageBlock,
  type JobListItemView,
  type JobSearchFilter,
  type PaymentHistoryItemView,
  type PaymentHistoryScope,
  type PaymentMonthGroupView,
} from "@makit/shared";

import { PageHead } from "../app/PageHead";
import { useSession } from "../app/SessionGate";
import { Button, FilterChip, Skeleton, StatusChip } from "../components/primitives";
import { LimitBanner } from "../components/banners";
import { EmptyState } from "../components/feedback";
import { SettingGroup, SettingRow, ToggleRow } from "../components/settings";
import { AmendmentReopenRow } from "../features/policy-amendment/AmendmentNotice";
import {
  accountLabel,
  fetchAccount,
  fetchJobBilling,
  fetchPaymentHistory,
  fetchRefundStatement,
  listJobs,
} from "../lib/api";
import { SETTLEMENT_PARAM } from "../lib/settlement";
import { PointsSection } from "../features/points/PointsSection";

/** `W-70` 마이페이지. 헤더의 계정 메뉴에서 들어오는 **전용 화면**이다.
 *
 * **정산 명세는 행 아래에서 제자리로 펼친다**(`⌄`, 열린 동안 `⌃`). 앱은 화면 이동(`›`)이고
 * 웹은 제자리 펼침이다 — 웹은 뒤 목록을 보면서 명세를 읽을 수 있고 폰은 그럴 폭이 없다.
 * **한 번에 한 행만 펼친다.** 둘이 동시에 열리면 금액이 두 벌 보여 어느 건인지 흐려진다.
 *
 * **작업 화면(`W-30`·`W-31`)의 정산 줄이 이리로 떨어진다.** 그 작업의 결제 행이 펼쳐진
 * 상태로 열리고 그 행으로 스크롤한다. 작업 단위 화면에서 계정 단위 기록으로 넘어오는
 * 유일한 길이고, 명세를 그쪽에서 다시 쓰지 않는 이유가 이 길이다(PRD 7.7.4 · 7.7.8). */
export function AccountScreen() {
  const navigate = useNavigate();
  /** 시행일 전의 개정. 없으면 다시 여는 줄이 없다. */
  const amendment = pendingAmendment();
  const [params] = useSearchParams();
  const wantedJobId = params.get(SETTLEMENT_PARAM);
  const { session, signOut: endSession } = useSession();
  /** 셀러가 들어온 수단의 낱말. **없으면 그 자리를 비운다.** 앱의 `A-40` 이 `…로 로그인됨`
   *  에서 하는 것과 같다. 게이트 안이라 실제로는 언제나 값이 있다. */
  const providerWord = session.provider ? IDENTITY_PROVIDER_LABEL[session.provider] : null;

  // **이 화면에서 표시용이 아닌 것이 여기 하나다.** `내 계정` 두 줄은 실제 계정에서 온다.
  const account = useQuery({ queryKey: ["auth", "account"], queryFn: fetchAccount });
  // 로그아웃은 게이트가 한다(`SessionGate` 의 `signOut`). 캐시 전부를 비우고 돌아갈 주소 없이
  // 로그인 화면으로 간다. 계정 메뉴의 로그아웃과 **같은 함수**다. 한 동작이 두 자리에서
  // 다르게 끝나지 않게.
  const signOut = useMutation({ mutationFn: endSession });

  // ⭐ 학습 이용은 **옵트인이고 기본이 꺼짐이다**(PRD `13.1` O-19 · O-38 확정, 목업 `W-70`).
  // 서버도 `trainingUseAllowed: false` 로 시작한다(#94). 이 스위치는 아직 저장되지 않는다.
  const [photoUse, setPhotoUse] = useState(false);
  const [openPayment, setOpenPayment] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  /** **이번 달 그룹이 세는 것은 작업이다. 청구 줄이 아니다.**
   *
   * ⛔ **청구 줄이 0이어도 이 숫자를 비우지 않는다**(PRD 7.7.8). 청구가 정해지기 전에 만든
   * 작업은 아래 목록에 서지 않으므로, 함께 비우면 **만든 작업이 화면에서 사라져** 셀러가
   * 자기 작업을 잃었다고 읽는다.
   *
   * **경계는 서버에 맡긴다** — `CUSTOM` 구간을 보내면 `jobEndpoints.list` 가 로컬 자정을
   * UTC 로 옮긴다. 앱의 `useMonthSummary` 와 같은 규약이라 같은 `9월 1일` 이 두 화면에서
   * 같은 구간이 된다. */
  const today = new Date();
  const monthFrom = isoDate(new Date(today.getFullYear(), today.getMonth(), 1));
  const monthTo = isoDate(today);
  const monthFilter: JobSearchFilter = {
    period: "CUSTOM",
    customFrom: monthFrom,
    customTo: monthTo,
    statusKeys: [],
    origins: [],
  };
  const monthJobs = useQuery({
    queryKey: ["jobs", "account", "month", monthFrom, monthTo],
    queryFn: () => listJobs(monthFilter, { limit: MONTH_PAGE_SIZE }),
  });
  const monthItems = monthJobs.data?.items ?? [];
  const monthJobCount = monthJobs.data?.matchedCount ?? 0;
  /** `작업 이력 관리` 줄의 값. 조건과 무관한 이 계정의 전체 작업 수(`totalCount`)다. 앱 `A-40`
   *  의 같은 줄과 같은 꼴(`N건`)이다. **셀 수 없으면 비운다**(불러오는 중 · 실패). 가짜 `18건` 을
   *  그리던 자리다(2026-10-04 걷음). */
  const allJobsValue = monthJobs.isSuccess ? `${monthJobs.data.totalCount}건` : undefined;

  /** 청구 줄. **범위 칩은 데이터를 보고 선다**(목업 `W-70` 의 `2026년 ⌄` · `전체` · `돌아간 것`.
   *  PRD 7.6.21 · 7.7.8).
   *
   * ⓐ `2026년 ⌄` 은 **고를 해가 둘 이상일 때만** 그린다. ⓑ `전체` · `돌아간 것` 은 **돌아간 줄이
   * 하나라도 있을 때 둘이 함께** 온다. 갈 곳 없는 꺾쇠와 누를 때마다 빈 목록을 내는 칩을 두지
   * 않는다(`7.5.4`). 결제가 2026-09-25 에 붙어 돌아간 줄이 실제로 생기므로 칩이 살아난다.
   *
   * **그 판단은 조건 없는 목록으로 한다.** 좁힌 목록으로 하면 `돌아간 것` 을 고른 순간 다른 해가
   * 사라져 기간 칩이 꺼지고, 셀러가 되돌릴 자리를 잃는다(`7.7.8` ②: 이때 범위 선택과 칩은
   * 남는다). 기간의 기본값은 올해다(목업의 `2026년`. 기본값 자체는 `D-44` 에서 다시 본다).
   * 올해 줄이 없으면 고를 수 있는 해 가운데 가장 최근 해다. */
  const baseHistory = useQuery({
    queryKey: ["payments", "history"],
    queryFn: () => fetchPaymentHistory(),
  });
  const baseGroups = baseHistory.data?.groups ?? [];
  const years = historyYears(baseGroups);
  const showYears = years.length >= 2;
  const showScopes = baseGroups.some((group) => group.items.some(isReturnedRow));
  const [pickedYear, setPickedYear] = useState<number | null>(null);
  const [pickedScope, setPickedScope] = useState<PaymentHistoryScope>("ALL");
  const thisYear = today.getFullYear();
  const year = !showYears
    ? null
    : pickedYear !== null && years.includes(pickedYear)
      ? pickedYear
      : years.includes(thisYear)
        ? thisYear
        : (years[0] ?? null);
  const scope: PaymentHistoryScope = showScopes ? pickedScope : "ALL";
  const narrowed = year !== null || scope !== "ALL";
  const narrowedHistory = useQuery({
    queryKey: ["payments", "history", scope, year],
    queryFn: () => fetchPaymentHistory({ filter: { scope, year } }),
    enabled: narrowed,
  });
  const history = narrowed ? narrowedHistory : baseHistory;
  const groups = history.data?.groups ?? [];
  const items = groups.flatMap((group) => group.items);
  const matchedCount = history.data?.matchedCount ?? 0;
  const totalCount = history.data?.totalCount ?? 0;
  /** **연도는 열 단위로 켠다**(PRD 7.5.13 규칙 4). 한 줄이라도 올해가 아니면 그 열 전부 켠다. */
  const withYear = needsYear(items.map(paymentHistoryOccurredAt));
  /** 서버가 자르기 전에 센 수보다 그려진 줄이 적으면 잘린 것이다(`PAYMENT_HISTORY_MAX_ROWS`). */
  const truncated = matchedCount > items.length;

  /** 정산 줄이 데려온 작업의 결제 id. **조용히 아무 일도 안 하지 않는다.** 누른 꺾쇠가 갈 곳
   *  없는 꺾쇠가 되면 셀러는 화면이 고장난 줄 읽는다. **아직 답이 안 왔을 때는 말하지
   *  않는다**(`isSuccess`). 불러오는 중을 "없다"로 말하면 있는 줄을 없다고 한다.
   *
   * ⚠️ **목록에서 찾던 것을 걷었다**(2026-09-14 이중 코드리뷰). `items` 는 서버가
   * `PAYMENT_HISTORY_MAX_ROWS`(500)에서 자른 뒤의 줄이라, **줄이 그 수를 넘은 셀러는 오래된
   * 작업의 명세를 영영 열지 못하고** 아래 `찾지 못했습니다` 를 봤다. 청구가 실재하는데 없다고
   * 말한 것이다. 상한을 쪽 나누기 대신으로 정한 결정과 이 조회가 서로를 모르고 있었다. */
  const jobBilling = useQuery({
    queryKey: ["payments", "job", wantedJobId],
    queryFn: () => fetchJobBilling(wantedJobId as string),
    enabled: !!wantedJobId,
  });
  const wantedPaymentId = jobBilling.data?.paymentId ?? null;
  const settlementMissing = wantedJobId !== null && jobBilling.isSuccess && !wantedPaymentId;

  /** 그 결제가 **지금 그려진 목록 안에 있는가.** 없으면 행을 펼칠 수가 없다(행이 DOM 에
   *  없다). 그때는 명세를 목록 위에 따로 세운다. 열쇠는 손에 쥐었는데 화면이 아무것도 안
   *  하면 위에서 고친 거짓말이 **침묵**으로 바뀔 뿐이다.
   *
   * **목록이 아직 오는 중이면 아무 쪽으로도 정하지 않는다.** 그 사이에 "목록에 없다"로 읽으면
   * 명세가 위에 한 번 섰다가 줄이 도착하는 순간 행 아래로 옮겨 간다. */
  const wantedInList =
    wantedPaymentId !== null && items.some((item) => item.paymentId === wantedPaymentId);
  const wantedOutsideList = wantedPaymentId !== null && !history.isPending && !wantedInList;

  useEffect(() => {
    if (!wantedPaymentId || !wantedInList) return;
    setOpenPayment(wantedPaymentId);
    rowRefs.current[wantedPaymentId]?.scrollIntoView({ block: "center" });
  }, [wantedPaymentId, wantedInList]);

  return (
    <>
      <PageHead
        frame="W-70"
        title="마이페이지"
        subtitle="앱의 마이페이지와 같은 내용입니다"
        // 표본 계정이 아니라 지금 로그인한 계정이다. 아래 `내 계정` 그룹과 같은 값을
        // 보여야 한다 — 머리와 본문이 다른 사람을 가리키면 그 화면은 못 믿는다.
        // 잇는 일은 `W-20` 의 계정 메뉴와 같은 함수가 한다.
        right={accountLabel(session)}
      />


      <div className="review" style={{ marginTop: 16 }}>
        <div>
          {/* `W-90` 을 시행일까지 다시 여는 줄(PRD `7.5.17` 요건 ⑤). **메뉴 목록 맨 위**다(design-manager 2026-10-28).
              띄울 개정이 없으면 줄째로 없다. */}
          {amendment ? (
            <div style={{ marginBottom: 14 }}>
              <SettingGroup>
                <AmendmentReopenRow amendment={amendment} onOpen={() => navigate("/account/notices/privacy")} />
              </SettingGroup>
            </div>
          ) : null}
          <div style={{ marginBottom: 14 }}>
            {/* **`쓴 금액` 줄을 걷었다** (확정 2026-09-13, PRD 7.7.8). ⓐ `비용`·`쓴` 은 나가지
                않은 돈을 썼다고 말하고, ⓑ 바로 아래 그 달의 `청구` 와 **글자 그대로 같은
                숫자**가 된다. 같은 숫자를 한 화면에 두 번 적지 않는다. */}
            <SettingGroup title="이번 달">
              <SettingRow label="만든 상세페이지" value={monthValue(monthJobs, monthJobCount, monthItems)} />
              {/* ⭐ **다음 상품으로 가는 입구** (2026-09-22 사용자 확정, 목업 `W-70`). 흐름은 `W-20`
                  의 올리기 자리가 이미 가지고 있어 그쪽으로 보낸다. 앱에는 두지 않는다(탭바의
                  `만들기` 가 이미 있다). */}
              <SettingRow
                label="새 상품 만들기"
                value="내 작업에서 사진 올리기"
                action="page"
                onClick={() => navigate("/jobs")}
              />
            </SettingGroup>
          </div>

          <PointsSection />
          <h4 className="hist-h">청구 내역</h4>
          {/* 목업 `W-70` 의 `fchips` 줄. 기간은 하나만 고르는 펼침 칩, 상태는 켜고 끄는 칩이다
              (PRD 7.6.21 「모양으로 성격을 가른다」). 둘 다 설 이유가 없으면 줄 자체가 없다. */}
          {showYears || showScopes ? (
            <div className="fchips" style={{ margin: "-6px 0 9px" }}>
              {year !== null ? (
                <YearPick years={years} value={year} onPick={setPickedYear} />
              ) : null}
              {showScopes
                ? PAYMENT_HISTORY_SCOPES.map((option) => (
                    <FilterChip
                      key={option.key}
                      label={option.label}
                      on={scope === option.key}
                      onClick={() => setPickedScope(option.key)}
                    />
                  ))
                : null}
            </div>
          ) : null}
          {settlementMissing ? (
            <LimitBanner>
              <b>그 작업의 청구 기록을 찾지 못했습니다.</b> 결제 전이거나 아직 정산이 만들어지지 않은
              작업입니다. 아래 목록에서 직접 찾아 주세요.
            </LimitBanner>
          ) : null}

          {/* 결제는 찾았는데 **그 행이 목록에 없다**(500줄에서 잘렸다). 행을 펼칠 수 없으므로
              명세를 여기 세운다. 목록에 있으면 이 자리는 비고 그 행이 펼쳐진다.
              같은 명세가 한 화면에 두 번 서지 않는다. */}
          {wantedOutsideList && wantedPaymentId ? (
            <SettlementDetail paymentId={wantedPaymentId} />
          ) : null}

          {history.isPending ? (
            /* 불러오는 중을 "줄이 없다"로 말하지 않는다. 그 문구가 번쩍이면 청구 줄을 가진
               셀러가 자기 기록이 사라진 줄 읽는다. */
            <div role="status" aria-live="polite" style={{ display: "grid", gap: 9 }}>
              <span className="sr-only">청구 내역을 불러오는 중입니다</span>
              <Skeleton width="34%" height={13} />
              <Skeleton width="88%" />
              <Skeleton width="72%" />
            </div>
          ) : history.isError ? (
            /* 조회가 실패한 것과 청구된 작업이 없는 것은 다른 사실이다. */
            <EmptyState
              art="!"
              tone="bad"
              title="청구 내역을 지금 불러올 수 없어요"
              body={
                <>
                  연결이 끊겼거나 잠깐 답이 오지 않았습니다.{" "}
                  <b>청구된 작업이 없어진 것이 아닙니다.</b>
                </>
              }
              action={<Button label="다시 시도" onClick={() => void history.refetch()} />}
            />
          ) : totalCount === 0 ? (
            /* ① 줄이 하나도 없다. **심볼을 두지 않는다** — 그룹 자리라 화면 전체가 빈 것이
               아니다(PRD 7.7.8). 제목은 "작업이 없다"가 아니라 **"청구된 작업이 없다"** 이고,
               둘째 줄은 **작업이 있는 셀러에게만** 참이다. `hasAnyJob` 이 `undefined` 면
               서버가 아직 말하지 않은 것이라 그리지 않는다. ⛔ `?? true` 로 채우지 마라. */
            <div className="blank">
              <span className="h">아직 청구된 작업이 없습니다</span>
              <span className="b">상품 사진을 올려 상세페이지를 만들면 그 건이 여기 줄로 섭니다.</span>
              {history.data?.hasAnyJob === true ? (
                <span className="b">
                  장당 청구가 정해지기 전에 만든 작업은 여기 줄로 서지 않습니다.
                </span>
              ) : null}
              <Button label="상품 사진 올리기" onClick={() => navigate("/jobs")} />
            </div>
          ) : matchedCount === 0 ? (
            /* ② 조건이 좁아서 비었다. 빈 상태가 아니라 **힌트**다 — 되돌릴 자리가 사라지면
               셀러가 갇힌다. 범위 선택이 서는 날 이 갈래가 실제로 생긴다. */
            <p className="hint">
              조건에 맞는 줄이 없습니다. 청구된 작업이 없는 게 아니라 조건이 좁습니다.
            </p>
          ) : (
            <SettingGroup bill>
              {/* **달이 여럿이면 여럿을 그린다. 웹이 이번 달에 갇히지 않는다**(PRD 7.7.8).
                  데이터는 하나인데 앱이 한 해를 펼치고 웹만 이번 달이면, 셀러는 지난 달
                  청구를 확인하려고 폰을 집어야 한다.
                  **`sticky` 를 쓰지 않는다** — 웹은 그룹이 아니라 페이지가 스크롤한다.
                  **월 끝의 합계 줄(`paysum`)은 없다** — 합계는 머리글 안에 있고, 같은 숫자를
                  한 화면에 두 번 두지 않는다(`A-43` 과 같은 규칙). */}
              {groups.map((group) => (
                <div key={group.yearMonth}>
                  <div className="paymonth">
                    <span className="pmm">{paymentMonthLabel(group.yearMonth)}</span>
                    <span className="pms">
                      청구 <span className="mono">{formatKrw(group.totalAmount)}</span>
                    </span>
                  </div>
                  {group.items.map((item) => {
                    const open = openPayment === item.paymentId;
                    const chip = paymentHistoryChip(item);
                    // 상품명 컬럼이 아직 비어 있다(`13.1` O-8). 앱의 같은 줄과 같은 말이어야 한다.
                    const title = item.productName ?? "새 상품";
                    // ⭐ **줄을 누르면 그 작업의 내보내기(`W-40`)로 간다** (2026-09-22 사용자 확정,
                    // 목업 `W-70` 캡션). 상품명 옆 꺾쇠에서 셀러가 기대하는 것은 그 상품의 결과물이다.
                    // **만든 장이 없는 줄(전량 실패 · 시작 전 취소)에는 `›` 를 붙이지 않는다**. 갈
                    // 곳 없는 꺾쇠를 두지 않는다(`A-41`).
                    const hasResult = item.billedQuantity > 0;
                    const openResult = () => navigate(`/jobs/${item.jobId}/export`);
                    return (
                      <div
                        key={item.paymentId}
                        ref={(el) => {
                          rowRefs.current[item.paymentId] = el;
                        }}
                      >
                        <div
                          className="payrow"
                          onClick={hasResult ? openResult : undefined}
                          style={hasResult ? undefined : { cursor: "default" }}
                        >
                          {/* 키보드로도 같은 곳에 간다. 이 버튼은 따로 동작하지 않고 누름이 줄로
                              그대로 올라간다. 한 줄에 같은 이동을 두 벌 적지 않는다. */}
                          {hasResult ? (
                            <button type="button" className="pn" style={ROW_BUTTON}>
                              {title}
                            </button>
                          ) : (
                            <span className="pn">{title}</span>
                          )}
                          {/* **화면이 곱하지 않는다.** 서버가 셈한 값을 그대로 적는다. */}
                          <span className="pa mono">
                            {formatKrw(item.netAmount)}
                            {hasResult ? (
                              <span className="chev" aria-hidden="true">
                                {" ›"}
                              </span>
                            ) : null}
                          </span>
                          {/* 보조줄도 앱과 같다 — `09-13 14:21 · 1장`. **시:분까지 적는 것도
                              같다.** 폭이 넓다고 이 화면만 형식을 되돌리면 같은 두 줄이
                              앱에서는 갈리고 웹에서는 안 갈린다(PRD 7.7.8). */}
                          <span className="pm mono">
                            {paymentHistoryDetailLine(item, { year: withYear })}
                          </span>
                          {/* **정산 명세의 손잡이는 상태 칩 옆 `⌄` 다** (2026-09-22 사용자 확정).
                              `›` 는 다른 화면으로 가고 `⌄` 는 이 자리에서 펼친다. 누름이 줄까지
                              올라가 화면을 옮기지 않게 막는다. */}
                          <button
                            type="button"
                            className="ps"
                            style={ROW_BUTTON}
                            aria-expanded={open}
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpenPayment(open ? null : item.paymentId);
                            }}
                          >
                            {/* 칩과 아래 명세가 같은 값에서 나온다. 손으로 적으면 칩은 `완료`
                                인데 펼치면 못 만든 장이 있는 행이 만들어진다. */}
                            <StatusChip tone={chip.tone} label={chip.label} />
                            <span className="chev" aria-hidden="true">
                              {open ? " ⌃" : " ⌄"}
                            </span>
                          </button>
                        </div>
                        {open ? (
                          <SettlementDetail
                            paymentId={item.paymentId}
                            name={title}
                          />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ))}
            </SettingGroup>
          )}

          {truncated ? (
            /* **범위 선택이 없을 때는 뒷 문장을 뺀다.** 없는 조작을 가리키지 않는다(PRD 7.5.4 ·
               7.6.21 · 7.7.8). */
            <p className="hint">
              최근 500줄만 보입니다.{showYears ? " 기간을 좁히면 나머지가 보입니다." : ""}
            </p>
          ) : null}

          {items.length > 0 ? (
            <>
              {/* ⛔ **결제 전을 알리던 두 문장(앱 푸터 배너와 같은 것)을 걷었다** (2026-10-04).
                  PRD 7.7.8 이 「이 줄은 결제가 붙는 날 사라진다. 첫 문장이 그날 거짓이 되기
                  때문이다」로 정해 두었고 결제는 2026-09-25 에 붙었다. 조건으로 좁혀 남기지
                  않는다. **줄의 돈 단계는 줄마다의 상태 칩(`결제 전` 등)이 말한다**(PRD 7.6.21). */}
              {/* 앞 두 문장은 목업 `W-70` 그대로다(2026-09-22 사용자 확정으로 손잡이가 둘이 됐다).
                  끝 문장은 2026-10-04 사용자 결정(환불정책 B안)으로 확정됐다. 공개 환불정책
                  `P-02` 제4조가 정본이고, 글자는 PRD `7.0` 「로그인 전 화면의 확정 문구」 (5) 2 다.
                  `Making.tsx` 전부 실패 배너와 **같은 상수**를 읽는다. 각자 적으면 한쪽만 고쳐진다.
                  ⛔ 옛 끝 문장(돈이 저절로 돌아가니 할 일이 없다는 말)으로 되돌리지 않는다. B안은
                  수동 취소이고, 알려 주면 더 빨리 간다는 길을 감추게 된다. */}
              <p className="hint">
                줄을 누르면 <b>그 상품의 만든 이미지</b>가 열립니다 <span className="chev">›</span>.
                상태 칩의 <span className="chev">⌄</span>를 누르면 <b>정산 명세</b>가 줄 아래에서
                펼쳐집니다. {UNMADE_SHEET_RETURN_NOTICE}
              </p>
            </>
          ) : null}
        </div>

        <div className="editpanel">
          {/* ⚠️ **우측 열의 맨 위다.** 이 그룹이 웹에 없으면 네아로 검수를 신청하지
              못한다 — 등록에 필요한 것이 **PC 웹의 서비스 URL** 이라 검수자가 보는 것이
              웹이고, 첨부물로 낼 회원정보수정 화면 캡처가 이 화면이 된다(PRD 7.7.8).
              **앱에만 두면 안 된다.**
              내용의 정본은 `A-48` 이고 여기서 값을 다시 정하지 않는다. 검수 서류가 요구하는
              것은 "실제 수집 항목"이라 **앱과 웹이 다른 목록을 보이면 그 자체가 불일치**다. */}
          <section>
            <h3>내 계정</h3>
            <SettingRow label="이름" value={accountValue(account, account.data?.displayName)} />
            <SettingRow label="이메일" value={accountValue(account, account.data?.email)} />
            {/* ⚠️ **낱말이 셀러가 들어온 계정을 따라간다** (2026-09-10 정정). 여기 `네이버 계정`
                이라고 굽혀 있었다. **카카오로 가입한 셀러에게 있지도 않은 계정에 가서 고치라고
                말하는 문장이었다.** 앱의 같은 자리(`A-40`)는 이미 `IDENTITY_PROVIDER_LABEL` 을
                본다. 낱말의 정본은 PRD `7.5.9` 용어 표이고 여기서 짓지 않는다.
                ⚠️ **이 화면이 네아로 검수에 낼 회원정보수정 화면이라** 문장 하나가 서류가 된다. */}
            <p className="hint sm">
              {providerWord ? `${providerWord} 계정에서` : "계정에서"} 바꾸면 다음 로그인 때
              따라옵니다. 여기서는 고칠 수 없습니다.
            </p>
            <p className="hint">
              프로필 사진 · 성별 · 생일 · 연령대 · 휴대전화번호는 받지 않습니다.
            </p>
            {/* **이 계정의 세션 전부가 끊긴다.** 앱에서 로그아웃하면 웹도 끊긴다고 화면에
                적기로 했고(A-48), 한쪽만 끊으면 그 문장이 거짓말이 된다. */}
            <SettingRow label="로그아웃" onClick={() => signOut.mutate()} />
          </section>

          <section>
            <h3>사진 이용</h3>
            <ToggleRow label="품질 개선에 내 사진 쓰기" on={photoUse} onChange={setPhotoUse} />
            {/* ⭐ **목업 `W-70` 글자 그대로**(앱 `A-41` 과 같은 문장). D-15 가 옵트인으로 닫혀
                옛 `목업대로 켠 상태로 두었고` 줄을 걷었다.
                ⚠️ `지금은 꺼져 있습니다.` 는 꺼져 있을 때만 띄운다. 켠 뒤에도 두면 화면이
                스위치와 반대말을 한다. 켜진 상태의 문장을 새로 짓지 않는다. */}
            <p className="hint">
              {photoUse ? "" : "지금은 꺼져 있습니다. "}켜면 그 뒤에 올리는 사진부터 씁니다. 품질
              개선에 아직 쓴 사진이 없습니다. 쓰기 시작하면 그 뒤로는 되돌릴 수 없습니다.
            </p>
            <p className="hint">
              켜도 더 드리는 것이 없습니다. 끄셔도 쓰실 수 있는 기능은 똑같고, 못 만든 장을 다시
              만들어 드리는 기간도 똑같습니다. 켜면 그 사진은 올린 날부터 30일이 지나도 품질
              개선용 사본으로 남고, 그 사본으로는 다시 만들어 드리지 않습니다.
            </p>
            <p className="hint">
              <b>이 스위치는 아직 저장되지 않습니다.</b>
            </p>
          </section>

          {/* ⛔ **「동의 기록」 섹션을 걷었다** (2026-10-04 사용자 지시 · design-manager 보수적 권고).
              줄은 예시(`이용약관 v1.2 09-01 09:12`)였고 동의는 서버에 담기지 않는다. 기록을 하나도
              보여 주지 않으면서 「기록」이라 부르면 거짓이 된다. 약관과 정책 링크는 같은 화면 맨
              아래 사업자 정보(`components/BusinessInfo.tsx`)에 이미 있다. */}
          <section>
            <h3>내 데이터</h3>
            {/* ⛔ 예시 값(`원본 사진 142장` · `결제 수단 등록 안 됨`)과 갈 곳 없는 꺾쇠 둘을 걷었다
                (2026-10-04). 원본 장수를 주는 서버 값이 웹에 없고, 결제 수단 화면은 `D-19` 로 없다.
                섹션 제목도 앱 `A-42` 의 「내 데이터」로 줄였다. */}
            <SettingRow
              label="작업 이력 관리"
              value={allJobsValue}
              action="page"
              onClick={() => navigate("/account/jobs")}
            />
            {/* ⭐ **보관 기간이 정해졌다**(2026-09-23 사용자 확정. 목업 `W-70` · `W-50`). 원본은
                작업마다 올린 날부터 30일이다. 만든 상세페이지는 **2026-10-10 사용자 결정**(토스 카드사 심사
                보완 요청)으로 「계정이 있는 동안」에서 「작업마다 결제한 날부터 1개월」로 바뀌었다. 목업 `W-70`
                은 아직 옛 값이고, 이 어긋남은 PRD 에 기록했다. 공개 화면의 정본은 `SERVICE_PERIOD`
                (`public-copy.ts`)와 처리방침 제3조다. */}
            <SettingRow label="원본 사진 보관" value="작업마다 올린 날부터 30일" />
            <SettingRow label="만든 상세페이지 보관" value="작업마다 결제한 날부터 1개월" />
          </section>
        </div>
      </div>

      {/* ⛔ **설계 메모 상자를 걷었다** (2026-10-04 사용자 지시). 「스위치와 링크는 아직 저장되지
          않습니다…」 배너, 「웹으로 가입한 셀러도 약속을 지킬 수 있어야 합니다」 콜아웃, 「이 화면에서
          아직 못 합니다」(미정 배지)는 설계자에게 하는 말이었다. 스위치가 저장되지 않는다는 사실은
          사진 이용 섹션의 「이 스위치는 아직 저장되지 않습니다.」가 그 자리에서 말한다. */}
    </>
  );
}

/** `내 계정` 두 줄의 값. **어떤 경우에도 빈 줄로 두지 않는다.**
 *
 * ⚠️ 이 화면이 네아로 검수에 낼 회원정보수정 화면이다(PRD 7.7.8). 조회가 실패했을 때
 * 빈 줄 둘이 남으면 검수자에게는 **"수집 항목이 없다"** 로 보이고, 그것은 바로 위 잔글씨
 * ("이름과 이메일만 받습니다")와 어긋난다. **못 불러왔다는 사실을 적는 편이 맞다.** */
function accountValue(
  state: { isPending: boolean; isError: boolean },
  value: string | null | undefined,
): string {
  if (state.isPending) return "불러오는 중";
  if (state.isError) return "불러오지 못했습니다";
  // ⚠️ **2026-09-10 부터 실제로 나오는 자리다.** 네이버는 이름과 이메일이 둘 다 있어야
  // 계정을 만들어 주지만(`PROFILE_DENIED`), **카카오는 이메일이 선택 동의라 없이도 들어온다**
  // (`apps/api/src/lib/oauth.ts` 의 `requestProfile`). 마스킹된 주소도 여기로 온다. 가려진
  // 값을 저장하지 않고 `null` 로 담기 때문이다. **앱의 같은 자리도 `없음` 이다.**
  return value ?? "없음";
}

/** 줄 안의 버튼이 줄의 생김새를 바꾸지 않게 브라우저 기본 모양을 걷는다. 글자 크기·굵기는
 *  `.payrow .pn` · `.ps` 가 정하므로 `font` 를 통째로 덮지 않는다. */
const ROW_BUTTON = {
  background: "none",
  border: 0,
  padding: 0,
  margin: 0,
  fontFamily: "inherit",
  color: "inherit",
  textAlign: "left",
  cursor: "pointer",
} as const;

/** `돌아간 것` 칩이 거르는 줄. **서버의 `scope=REFUNDED` 와 같은 조건이다**
 *  (`apps/api/src/routes/payments.ts`). 둘이 다르면 칩은 섰는데 누르면 빈 목록이 나온다. */
function isReturnedRow(item: PaymentHistoryItemView): boolean {
  return item.status === "PARTIALLY_REFUNDED" || item.status === "REFUNDED";
}

/** 조건 없는 목록에 줄이 있는 해. 큰 해가 앞이다. 달 묶음(`2026-09`)의 앞 네 자리를 읽는다. */
function historyYears(groups: PaymentMonthGroupView[]): number[] {
  const seen = new Set<number>();
  for (const group of groups) {
    const year = Number(group.yearMonth.slice(0, 4));
    if (Number.isInteger(year)) seen.add(year);
  }
  return [...seen].sort((a, b) => b - a);
}

/** `2026년 ⌄` 펼침 칩. 하나만 고른다. 열린 모양은 `W-55` 의 찾기 조건과 같은 `webdrop` 이다
 *  (웹은 시트 대신 제자리에서 연다). Esc 와 바깥 누름으로 닫힌다. */
function YearPick({
  years,
  value,
  onPick,
}: {
  years: number[];
  value: number;
  onPick: (year: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    function onDown(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  return (
    <span className="dropwrap" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="fchip on"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {value}년{" "}
        <span className="chev" aria-hidden="true">
          ⌄
        </span>
      </button>
      {open ? (
        <span className="webdrop fromleft" role="group" aria-label="기간">
          <span className="fchips">
            {years.map((candidate) => (
              <FilterChip
                key={candidate}
                label={`${candidate}년`}
                on={candidate === value}
                onClick={() => {
                  onPick(candidate);
                  setOpen(false);
                  buttonRef.current?.focus();
                }}
              />
            ))}
          </span>
        </span>
      ) : null}
    </span>
  );
}

/** 이번 달 요약이 한 번에 훑는 줄 수. **이 값이 곧 장수를 셀 수 있는 상한이다** — 목록
 *  응답에 장수 합계가 없어 받은 쪽 안에서만 셀 수 있다(앱의 `SUMMARY_PAGE_SIZE` 와 같다). */
const MONTH_PAGE_SIZE = 100;

/** 로컬 달력의 `YYYY-MM-DD`. **`toISOString()` 을 쓰지 않는다** — 그쪽은 UTC 라 월초·월말
 *  자정 근처에서 하루가 밀린다. */
function isoDate(date: Date): string {
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `이번 달` 줄의 값. **건수는 서버가 세고, 장수는 받은 쪽이 전체를 덮을 때만 적는다.**
 *  적게 나온 숫자를 그냥 적으면 열람 요구에 거짓으로 답하는 셈이 된다(PRD 12. 요구 13).
 *  앱의 `job-queries.ts` 와 같은 규칙이고, 낱말도 `accountValue` 와 같은 둘을 쓴다. */
function monthValue(
  state: { isPending: boolean; isError: boolean },
  jobCount: number,
  items: JobListItemView[],
): string {
  if (state.isPending) return "불러오는 중";
  if (state.isError) return "불러오지 못했습니다";
  if (items.length < jobCount) return `${jobCount}건`;
  const photos = items.reduce((sum, job) => sum + job.imageTotalCount, 0);
  return `${jobCount}건 · 사진 ${photos}장`;
}

// ⚠️ **월 머리글·상태 칩·보조줄·못 만든 장 라벨을 여기서 만들지 않는다** (2026-09-14 이중
// 코드리뷰). 넷 다 `packages/shared/src/billing-history.ts` 에 있고 `A-43` 이 같은 함수를
// 부른다. 그전에는 앱과 웹이 **글자 그대로 같은 구현을 한 벌씩** 들고 있었고, 두 파일이
// 나란히 "앱과 같아야 한다"고 주석으로 적어 두고 있었다. **적어서 지키는 것이
// `packages/shared` 를 둔 이유와 정반대다.**

/** `A-43b` 와 **같은 네 줄 + 처리 세 줄**. 웹이 따로 계산해 적지 않는다.
 *
 * **못 만든 장이 없으면 금액 줄이 하나다**(확정 2026-09-08, PRD 7.6.21a).
 *
 *  * `못 만든 0장 ₩0` 과 `돌아간 금액 ₩0` 을 그리지 않는다. **돌아가지 않은 것을 `돌아감`
 *    이라는 낱말로 말하는 것**이고, `A-32` 가 이미 "돌아간 돈이 없으면 돌아감을 말하지
 *    않는다"로 확정해 뒀다.
 *  * `만든 8장` 줄도 그리지 않는다. **전부 만들었으면 `결제한 금액`과 같은 숫자**라 한 줄이
 *    같은 사실을 두 번 적는다.
 *
 * `7.12` 요구 6과 부딪히지 않는다 — **그건 데이터 요구다. 데이터에는 `0` 이 있고 화면에는
 * 그 줄이 없다.** 마지막 줄의 이름은 아직 미정이다(`D-50`). */
function SettlementDetail({
  paymentId,
  name,
}: {
  paymentId: string;
  name?: string;
}) {
  // **명세를 웹이 따로 계산하지 않는다.** 앱과 **같은 주소**를 불러 같은 값을 받는다 —
  // 같은 금액을 두 화면이 각자 셈하면 한 자리만 어긋나도 셀러는 어느 쪽이 맞는지 모른다.
  const statement = useQuery({
    queryKey: ["payments", "statement", paymentId],
    queryFn: () => fetchRefundStatement(paymentId),
  });
  const data = statement.data;
  const failed = data?.failedLines ?? [];
  const block = refundedImageBlock(failed);
  // **돌아간 것이 없으면 돌아감을 말하지 않는다**(확정 2026-09-08, PRD 7.6.21a).
  const returned = failed.length > 0 || (data?.refundedAmount ?? 0) > 0;
  // `언제 · 어디로` 는 채울 값이 있을 때만 선다. 결제사가 붙기 전에는 세 칸이 다 비어 있고,
  // 빈 줄 셋은 "여기 무엇인가 있는데 안 보인다"로 읽힌다.
  const processingRows = [
    { label: "요청", value: formatDateTime(data?.requestedAt ?? "") },
    { label: "보낸 시각", value: formatDateTime(data?.sentAt ?? "") },
    { label: "돌아가는 곳", value: data?.destinationLabel ?? "" },
  ].filter((row) => row.value !== "");

  return (
    <div className="settlement">
      {/* 행 아래에서 펼칠 때는 그 행의 제목을 그대로 받는다. 목록 밖에 홀로 설 때는 받을
          행이 없어 명세가 들고 온 상품명을 쓴다. **둘 다 상품명이 비면 `새 상품` 이다.**
          목록 줄과 같은 말이다(`13.1` O-8). */}
      <h4 className="hist-h">
        {/* ⛔ 제목 뒤 `실행 방식 미정` 배지를 걷었다 (2026-10-04). 돈이 돌아가는 시점은 아래
            `REFUND_ARRIVAL_NOTICE` 가 공개 환불정책과 같은 말로 한다. */}
        정산 명세 · {name ?? data?.productName ?? "새 상품"}
      </h4>

      {statement.isPending ? (
        <div role="status" aria-live="polite" style={{ display: "grid", gap: 7 }}>
          <span className="sr-only">정산 명세를 불러오는 중입니다</span>
          <Skeleton width="70%" />
          <Skeleton width="52%" />
        </div>
      ) : statement.isError || !data ? (
        <p className="hint">
          <span className="l1">
            <b>정산 명세를 지금 불러올 수 없습니다.</b> 연결이 끊겼거나 잠깐 답이 오지 않았습니다.
          </span>
          <span className="l2">
            <Button label="다시 시도" small onClick={() => void statement.refetch()} />
          </span>
        </p>
      ) : (
        <>
          <SettingGroup bill>
            <SettingRow label="결제한 금액" value={formatKrw(data.paidAmount)} />
            {returned ? (
              <>
                {/* `만든 n장` 은 못 만든 장이 있을 때만 뜻이 있다. 전부 만들었으면 위 줄과
                    같은 숫자라 한 줄이 같은 사실을 두 번 적는다. */}
                <SettingRow
                  label={`만든 ${data.madeQuantity}장`}
                  value={formatKrw(data.madeAmount)}
                />
                {/* ⛔ **줄마다 금액을 찍지 않는다.** 2026-09-27 까지 이 자리는 못 만든 장을
                    파일 이름(`IMG_4024`)으로 한 줄씩 세우고 금액에 취소선을 그었다. **목업에
                    그 모양이 없다** — `.setgrp.bill` 안의 그 줄은 `못 만든 n장` 하나이고 값은
                    돌아간 금액이다(`A-43b`·`A-43c`). */}
                {block ? (
                  <SettingRow
                    // 한 장이면 라벨이 그 장까지 말한다. ⚠️ 그 이름은 **라벨 안**이다 —
                    // 이 컴포넌트의 값 칸은 언제나 `mono` 라 거기로 보내면 금액이 밀려난다.
                    label={
                      block.trailing ? (
                        <>
                          {block.head} <span className="mono">{block.trailing.mono}</span>
                        </>
                      ) : (
                        block.head
                      )
                    }
                    // ⛔ `failedLines` 의 금액을 더해 만들지 않는다(14.3 ③).
                    value={formatKrw(data.refundedAmount)}
                  />
                ) : null}
                {/* ⚠️ **여러 장이 돌아간 건의 목록이 여기 없다.** 앱은 `A-43c` 로 그 모양을
                    갖췄지만 **목업이 웹 쪽을 아직 미정으로 두었다**(`ver2-ondora-ui.html`:
                    "앱은 여러 장이 돌아간 건을 A-43c 로 그렸는데 **웹에는 그 모양이 없습니다**").
                    웹의 명세는 줄 아래에서 **제자리로 펼치는** 모양이라 그 안에 그룹을 하나 더
                    세우는 것이 레이아웃 결정이다. ⛔ **여기서 지어내지 않는다** —
                    `design-manager` 가 그 모양을 그리면 `block.rows` 를 그대로 받아 붙인다.
                    지금 웹은 **수와 금액까지만** 말하고 어느 장인지는 앱에서 본다. */}
                <SettingRow
                  label="돌아간 금액"
                  value={formatKrw(data.refundedAmount)}
                  tone="total"
                />
              </>
            ) : null}
            {processingRows.map((row) => (
              <SettingRow key={row.label} label={row.label} value={row.value} />
            ))}
          </SettingGroup>

          {/* `A-43c` 의 두 문장을 앱(`apps/mobile/src/app/my/refund.tsx`)과 **같은 글자, 같은
              조건**으로 세운다. 조건은 둘 다 `refundedImageBlock` 이 가른 「못 만든 장이 여럿」
              (`block.rows.length > 0`)이다. 한 장이면 `rows` 가 비고 `A-43b` 에는 두 문장이
              없다. 앱은 뒤 문장을 `returned` 안에 두지만 여럿이면 `failed.length > 0` 이라
              `returned` 가 늘 참이다. 그래서 웹은 같은 조건 하나로 둘을 묶어 세운다.
              앞 문장은 앱에서 `못 만든 장` 그룹의 평문 `note` 이고, 경고 톤은 뒤 문장만 쓴다.
              웹은 그 그룹(장 목록)의 모양이 아직 미정이라(위 `돌아간 금액` 줄 앞 주석) 앞 문장을
              평문 `hint` 로만 세운다. 그룹을 그리면 이 문장은 그 그룹의 끝으로 옮긴다. */}
          {block && block.rows.length > 0 ? (
            <>
              <p className="hint">
                사유가 달라도 돌아가는 금액은 같습니다. 결과를 못 드린 장은 받는 금액이 없습니다.
              </p>
              <LimitBanner>
                아직 만들지 않은 장은 여기 없습니다. 미뤄진 장은 돈이 돌아가지 않습니다.
              </LimitBanner>
            </>
          ) : null}

          {returned ? (
            // 2026-10-04 확정(PRD `7.0` 「로그인 전 화면의 확정 문구」 (5) 3). 앞 문장은 환불정책
            // `P-02` 제4조 끝 문장과 **같은 상수**다. 결제사 · 카드사 쪽 시간이지 우리 처리 기한이
            // 아니다. 옛 `기간 미정` 배지와 둘째 줄(실행 방식이 정해지면 말이 바뀐다)은 걷었다.
            // 제4조가 「취소합니다」로 공개 약속을 정해 가승인 갈래를 셀러에게 설명할 이유가 없다.
            // ⚠️ 「보낸 시각」 블록은 토스 관리자 화면에서 한 수동 취소를 모른다(같은 절 (5) 아래).
            <p className="hint">
              <span className="l1">
                {REFUND_ARRIVAL_NOTICE} 위의 보낸 시각은 우리가 보낸 때이고, 결제 수단에 찍히는
                날짜는 그보다 뒤입니다.
              </span>
            </p>
          ) : null}
          {/* ⛔ **결제 전 줄에 붙이던 「이 금액은 나가지 않았습니다」를 걷었다** (2026-10-04,
              PRD 7.7.8: 결제가 붙는 날 사라진다). 명세는 상태 칩 `⌄` 를 눌러 그 줄 바로 아래에
              펼쳐지므로, 줄의 돈 단계는 바로 위의 칩(`결제 전` 등)이 말한다. */}
        </>
      )}
    </div>
  );
}
